import { IconPlusOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { useMemo, useRef, useState } from "react";
import { computeNodeStepNumbers, extractWorkflowVariables, type NodeDefinition, type WorkflowVariable } from "./extract-workflow-variables";
import { getFriendlyLabel } from "@/components/ui/workflow-step-row/components/VariablePill";
import { useInputSchema } from "./InputSchemaContext";
import type { Edge, Node } from "@xyflow/react";
interface ArrayInputProps {
  label: string;
  description?: string;
  value: (string | number)[];
  onChange: (value: (string | number)[]) => void;
  itemType?: string;
  currentNodeId?: string;
  allNodes?: Node[];
  allEdges?: Edge[];
  nodeDefinitions?: NodeDefinition[];
}
export default function ArrayInput({
  label,
  description,
  value,
  onChange,
  itemType,
  currentNodeId,
  allNodes = [],
  allEdges = [],
  nodeDefinitions = []
}: ArrayInputProps) {
  const safeValue = Array.isArray(value) ? value : [];
  const [inputValue, setInputValue] = useState("");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const commandListRef = useRef<HTMLDivElement>(null);
  const inputSchema = useInputSchema();
  const stepNumbers = useMemo(() => computeNodeStepNumbers(allNodes, allEdges), [allNodes, allEdges]);
  const availableVariables = useMemo(() => {
    if (!currentNodeId || allNodes.length === 0) return [];
    return extractWorkflowVariables(currentNodeId, allNodes, allEdges, nodeDefinitions, undefined, inputSchema, stepNumbers);
  }, [currentNodeId, allNodes, allEdges, nodeDefinitions, inputSchema, stepNumbers]);
  const searchQuery = inputValue.replace("{{", "").toLowerCase();
  const filteredVariables = useMemo(() => {
    if (!dropdownVisible) return [];
    return availableVariables.filter(v => {
      if (!searchQuery) return true;
      const inner = v.id.slice(2, -2);
      const fieldName = inner.split(".").pop()?.toLowerCase() || "";
      return fieldName.includes(searchQuery) || v.label.toLowerCase().includes(searchQuery) || (v.displayLabel?.toLowerCase().includes(searchQuery) ?? false);
    });
  }, [searchQuery, availableVariables, dropdownVisible]);
  const handleAdd = () => {
    if (inputValue.trim()) {
      const trimmed = inputValue.trim();
      const isNumeric = itemType === "number" || itemType === "integer";
      // Don't coerce variables to numbers
      if (isNumeric && !trimmed.startsWith("{{")) {
        const num = Number(trimmed);
        if (isNaN(num)) return;
        onChange([...safeValue, num]);
      } else {
        onChange([...safeValue, trimmed]);
      }
      setInputValue("");
      setDropdownVisible(false);
    }
  };
  const handleRemove = (index: number) => {
    onChange(safeValue.filter((_, i) => i !== index));
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    if (val.includes("{{") && availableVariables.length > 0) {
      setDropdownVisible(true);
      setSelectedIndex(0);
    } else {
      setDropdownVisible(false);
    }
  };
  const handleVariableSelect = (variableId: string) => {
    onChange([...safeValue, variableId]);
    setInputValue("");
    setDropdownVisible(false);
  };
  const handleKeyDown = (e_0: React.KeyboardEvent) => {
    if (dropdownVisible && filteredVariables.length > 0) {
      if (e_0.key === "ArrowDown") {
        e_0.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredVariables.length);
        return;
      }
      if (e_0.key === "ArrowUp") {
        e_0.preventDefault();
        setSelectedIndex(prev_0 => (prev_0 - 1 + filteredVariables.length) % filteredVariables.length);
        return;
      }
      if (e_0.key === "Enter" || e_0.key === "Tab") {
        e_0.preventDefault();
        const selected = filteredVariables[selectedIndex] ?? filteredVariables[0];
        if (selected) handleVariableSelect(selected.id);
        return;
      }
      if (e_0.key === "Escape") {
        e_0.preventDefault();
        setDropdownVisible(false);
        return;
      }
    }
    if (e_0.key === "Enter") {
      e_0.preventDefault();
      handleAdd();
    }
  };
  const handleBlur = () => {
    setTimeout(() => {
      setDropdownVisible(false);
    }, 200);
  };

  /** Render a tag — show friendly label for variables, raw value otherwise */
  const renderTag = (item: string | number) => {
    const str = String(item);
    if (str.startsWith("{{") && str.endsWith("}}")) {
      const inner_0 = str.slice(2, -2);
      const friendly = getFriendlyLabel(inner_0);
      return friendly || str;
    }
    return str;
  };

  /** Check if a tag is a variable for distinct styling */
  const isVariable = (item_0: string | number) => {
    const str_0 = String(item_0);
    return str_0.startsWith("{{") && str_0.endsWith("}}");
  };
  return <div className="space-y-2">
      {label && <label className="field-label block">{label}</label>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      {/* Tags Display */}
      {safeValue.length > 0 && <div className="flex flex-wrap gap-2">
          {safeValue.map((item_1, index_0) => <div key={index_0} className={`flex items-center gap-1 rounded-md px-2 py-1 text-sm ${isVariable(item_1) ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" : "bg-blue-100 text-blue-700 dark:bg-blue-600/20 dark:text-blue-400"}`}>
              <span>{renderTag(item_1)}</span>
              <button type="button" onClick={() => handleRemove(index_0)} className="rounded hover:bg-blue-200 dark:hover:bg-blue-600/30">
                <IconXmarkOutline18 className="h-3 w-3" />
              </button>
            </div>)}
        </div>}
      {/* Input with variable dropdown */}
      <div ref={containerRef} className="relative flex gap-2">
        <Input type="text" value={inputValue} onChange={handleInputChange} onKeyDown={handleKeyDown} onBlur={handleBlur} placeholder="Type {{ for variables, or Enter to add" className="text-ds-text-primary flex-1" />
        <button type="button" onClick={handleAdd} className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700">
          <IconPlusOutline18 className="h-4 w-4" />
        </button>

        {/* Variable Suggestions Dropdown */}
        {dropdownVisible && filteredVariables.length > 0 && <div onMouseDown={e_1 => {
        e_1.preventDefault();
        e_1.stopPropagation();
      }} className="absolute left-0 top-full z-50 mt-1">
            <Command shouldFilter={false} className="border-ds-border-default bg-ds-surface-card max-h-[280px] w-[360px] overflow-auto rounded-lg border shadow-xl">
              <CommandList ref={commandListRef}>
                {Object.entries(filteredVariables.reduce((acc, v_0) => {
              if (!acc[v_0.category]) acc[v_0.category] = [];
              acc[v_0.category].push(v_0);
              return acc;
            }, {} as Record<string, WorkflowVariable[]>)).map(([category, vars]) => <div key={category} className="border-ds-neutral-100 dark:border-ds-neutral-700/50 border-b last:border-0">
                    <div className="text-ds-text-secondary dark:bg-ds-neutral-900 sticky top-0 z-10 bg-zinc-50 px-3 py-2 text-xs font-semibold">
                      {category}
                    </div>
                    <CommandGroup>
                      {vars.map(variable => <CommandItem key={variable.id} value={variable.id} onSelect={() => handleVariableSelect(variable.id)} data-highlighted={variable.id === filteredVariables[selectedIndex]?.id ? "true" : undefined} className={`cursor-pointer px-3 py-2 ${variable.id === filteredVariables[selectedIndex]?.id ? "dark:!bg-ds-neutral-700 !bg-blue-50" : ""}`}>
                          <div className="flex flex-col gap-1">
                            <span className="rounded-ds-xs inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-50 to-indigo-100 px-2 py-1 font-mono text-sm text-blue-700 dark:from-blue-950/30 dark:to-indigo-950/30 dark:text-blue-400">
                              {variable.displayLabel || variable.id}
                            </span>
                            <span className="label-xs">{variable.label}</span>
                          </div>
                        </CommandItem>)}
                    </CommandGroup>
                  </div>)}
              </CommandList>
            </Command>
          </div>}
      </div>
    </div>;
}